// src/pages/Wishlist.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Heart, ShoppingCart, Trash2, ArrowLeft, Star
} from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';

const Wishlist = () => {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { wishlistItems, removeFromWishlist } = useWishlist();

  if (wishlistItems.length === 0) {
    return (
      <div className="min-h-screen py-12 anim-fadeIn" style={{ backgroundColor: 'var(--color-body-bg)' }}>
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div
            className="rounded-2xl p-12 anim-scaleIn"
            style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)` }}
          >
            <Heart size={80} className="mx-auto mb-4 anim-float" style={{ color: 'var(--color-secondary-text)' }} />
            <h2 className="text-2xl font-bold mb-6" style={{ color: 'var(--color-primary-text)' }}>
              Your Wishlist is Empty
            </h2>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold btn-2d shadow-lg"
              style={{ backgroundColor: 'var(--color-button-bg)', color: 'var(--color-button-text)' }}
            >
              <ShoppingCart size={18} /> Start Shopping
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-6 anim-fadeIn" style={{ backgroundColor: 'var(--color-body-bg)' }}>
      <div className="max-w-7xl mx-auto px-4 md:px-6">

        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-full btn-2d"
            style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)`, color: 'var(--color-primary-text)' }}
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold" style={{ color: 'var(--color-primary-text)' }}>
              My Wishlist
            </h1>
            <p className="text-sm" style={{ color: 'var(--color-secondary-text)' }}>
              {wishlistItems.length} {wishlistItems.length === 1 ? 'item' : 'items'}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {wishlistItems.map((item, i) => (
            <div
              key={item.id}
              className="rounded-xl overflow-hidden card-lift anim-fadeInUp relative"
              style={{
                backgroundColor: 'var(--color-card-bg)',
                border: `1px solid var(--color-border)`,
                animationDelay: `${i * 80}ms`,
              }}
            >
              {/* Remove button */}
              <button
                onClick={() => removeFromWishlist(item.id)}
                className="absolute top-3 right-3 rounded-full p-2 shadow-md z-10 hover:scale-110 transition btn-2d"
                style={{ backgroundColor: 'var(--color-card-bg)' }}
                title="Remove from wishlist"
              >
                <Trash2 size={16} className="text-red-500" />
              </button>

              {/* Image */}
              <Link to={`/product/${item.id}`}>
                <div className="overflow-hidden bg-gray-100">
                  <img
                    src={item.image || 'https://via.placeholder.com/300'}
                    alt={item.title}
                    className="w-full h-40 md:h-48 object-cover hover:scale-110 transition-transform duration-500"
                  />
                </div>
              </Link>

              {/* Details */}
              <div className="p-3">
                <p className="text-xs font-semibold uppercase mb-1" style={{ color: 'var(--color-link)' }}>
                  {item.category}
                </p>
                <Link to={`/product/${item.id}`}>
                  <h3 className="font-bold text-sm line-clamp-2 mb-2 hover:opacity-80" style={{ color: 'var(--color-primary-text)' }}>
                    {item.title}
                  </h3>
                </Link>
                <div className="flex items-baseline gap-2 mb-3">
                  <span className="font-bold text-sm" style={{ color: 'var(--color-link)' }}>
                    Rs. {item.price}
                  </span>
                  {item.originalPrice > item.price && (
                    <span className="text-xs line-through" style={{ color: 'var(--color-secondary-text)' }}>
                      Rs. {item.originalPrice}
                    </span>
                  )}
                </div>

                <button
                  onClick={() => addToCart(item, 1)}
                  className="w-full py-2 rounded-lg flex items-center justify-center gap-2 text-xs font-semibold btn-2d"
                  style={{ backgroundColor: 'var(--color-button-bg)', color: 'var(--color-button-text)' }}
                >
                  <ShoppingCart size={14} /> Add to Cart
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Wishlist;